import { useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateClient } from "@/hooks/use-clients";
import { describeCallableError } from "@/lib/callables";
import type { AppUser } from "@/types";

/**
 * Correção de dados do cliente.
 *
 * O e-mail fica de fora: ele é a credencial no Firebase Auth, e mudá-lo no
 * documento sem mudar na autenticação deixaria os dois divergentes, com o
 * login continuando pelo antigo. Trocar e-mail é assunto de conta, não de
 * cadastro.
 */
export function ClientEditDialog({
  client,
  open,
  onOpenChange,
}: {
  client: AppUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateClient();
  const [form, setForm] = useState(() => ({
    name: client.name ?? "",
    phone: client.phone ?? "",
    cpf: client.cpf ?? "",
  }));

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await update.mutateAsync({
        userId: client.id,
        data: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          cpf: form.cpf.replace(/\D/g, ""),
        },
      });
      toast.success("Cadastro atualizado.");
      onOpenChange(false);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Corrigir cadastro</DialogTitle>
          <DialogDescription>
            O e-mail não é editável aqui: ele é a credencial de acesso do cliente.
          </DialogDescription>
        </DialogHeader>

        <form id="client-edit" onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="c-name">Nome</Label>
            <Input
              id="c-name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="c-phone">Telefone</Label>
              <Input
                id="c-phone"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-cpf">CPF</Label>
              <Input
                id="c-cpf"
                value={form.cpf}
                inputMode="numeric"
                onChange={(e) => setForm((p) => ({ ...p, cpf: e.target.value }))}
              />
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="client-edit" disabled={update.isPending}>
            {update.isPending ? <Loader2 className="animate-spin" /> : null}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
